import find from 'lodash/find';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useConfig } from '../../hooks';
import { useStores } from '../../stores';
import {
  OVERLAP_MENU_LIVE_CHAT_FUNCTION,
  PUBSUB_LIVE_CHAT_BUTTON_CLICKED,
  sanitizeHTML
} from '../../utilities';
import ButtonBase from '../ButtonBase';
import BackIcon from '../Icons/ChevronLeft';
import ChevronRightIcon from '../Icons/ChevronRightBold';
import MessageList from '../MessageList';
import Modal from '../Modal';
import OverlapMenuInnerWrapper from '../OverlapMenuInnerWrapper';
import Scrollbar from '../Scrollbar';

const OverlapMenuContent = ({ openChat }) => {
  const { ui, bot, chat, chatWindow } = useStores();
  const {
    header: { background: headerBackground, color: headerColor },
    window: {
      colors: { main: mainColor }
    },
    overlapMenu: {
      contentImage,
      menuSubtitle,
      contentAdditionalText,
      maxCountMainLayerItems,
      items,
      size,
      mobileView,
      colors,
      backdrop,
      options
    },
    scrollBar
  } = useConfig();
  const { t } = useTranslation();
  const initBreadCrumbItems = ['home'];
  const [itemChildren, setItemChildren] = React.useState([]);
  const [prevItems, setPrevItems] = React.useState([]);
  const [breadCrumbItems, setBreadCrumbItems] = React.useState(initBreadCrumbItems);
  const [page, setPage] = React.useState(0);
  const scrollbarRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const maxMainLayerItems = maxCountMainLayerItems || 4;
  const mainLayerItesm =
    items.length > maxMainLayerItems ? items.slice(0, maxMainLayerItems) : items;
  const itemColors = {
    mainColor: colors.itemText,
    mainBackground: colors.itemBackground,
    hoverColor: colors.itemTextHover || headerColor,
    hoverBackground: colors.itemBackgroundHover || headerBackground
  };

  const handleClickMenuItem = menuItem => {
    // the live chat opens (a specific feature)
    if (menuItem[OVERLAP_MENU_LIVE_CHAT_FUNCTION]) {
      PubSub.publish(PUBSUB_LIVE_CHAT_BUTTON_CLICKED);
    }

    // display the next layer (the item has children)
    if (menuItem.children.length > 0) {
      setPage(page + 1);
      setItemChildren(menuItem.children);

      // Add bread crumbs
      const breadCrumbs = breadCrumbItems;
      breadCrumbs.push(menuItem.label);
      setBreadCrumbItems(breadCrumbs);

      // Add layer items
      const newPrevItems = prevItems.filter(item => item.id < page + 1);
      newPrevItems.push({
        id: page + 1,
        items: menuItem.children
      });
      setPrevItems(newPrevItems);

      if (menuItem.categoryId) {
        bot.setParentCategoryId(menuItem.categoryId);
      }
      return ui.setOverlapMenuModal(true);
    }

    // open MessageList
    if (menuItem.categoryId) {
      return ui.setCategory(menuItem.categoryId, menuItem.label);
    }

    // open the joint-poly Chatbot (the iframe)
    return openChat();
  };

  const closeModal = () => {
    ui.setOverlapMenuModal(false);
    bot.setParentCategoryId(0);
    setItemChildren([]);
    setPrevItems([]);
    setBreadCrumbItems(initBreadCrumbItems);
    setPage(0);
  };

  const handleBackdropClick = () => {
    if (!backdrop.backdropClick) return null;

    return closeModal();
  };

  const handleBackClick = () => {
    if (page - 1 === 0) {
      return closeModal();
    }

    const breadCrumbs = breadCrumbItems;
    breadCrumbs.pop();
    setBreadCrumbItems(breadCrumbs);

    const prevItem = find(prevItems, item => item.id === page - 1);
    setPage(page - 1);
    return setItemChildren(prevItem.items);
  };

  // After click on Home button
  React.useEffect(() => {
    if (page > 0 && !ui.isOpenOverlapMenuModal && itemChildren.length > 0) {
      setItemChildren([]);
      setPrevItems([]);
      setBreadCrumbItems(initBreadCrumbItems);
      setPage(0);
    }
  }, [initBreadCrumbItems, itemChildren, page, prevItems, ui.isOpenOverlapMenuModal]);

  // Add click 'go to Home' to the button inside the no-response message
  React.useEffect(() => {
    const rootEl = containerRef;
    if (chat.messages.length > 0) {
      const buttons = rootEl.current.querySelectorAll('.overlapMenuHome');
      if (buttons.length > 0) {
        buttons.forEach(button => {
          button.onclick = e => ui.closeOverlapMenuModal();
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.messages.length]);

  // Show the main layer after closing the chat
  React.useEffect(
    () =>
      reaction(
        () => chatWindow.isClosed,
        isClosed => {
          if (isClosed) {
            ui.closeOverlapMenuModal();
          }
        },
        { delay: 300 }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Root ref={containerRef}>
      {ui.category.id || ui.isStartChatWithoutCategory ? (
        <MessageList />
      ) : (
        <>
          <StyledScrollbar
            renderView={({ style, ...props }) => {
              return (
                <ScrollbarView
                  {...props}
                  style={{
                    ...style,
                    overflowX: 'hidden',
                    bottom: '8px',
                    top: 0
                  }}
                />
              );
            }}
            autoHide
            ref={scrollbarRef}
            {...scrollBar}
          >
            <Wrapper
              contentPadding={ui.isMobile ? mobileView.contentPadding : size.contentPadding}
              mainColor={mainColor}
            >
              {contentImage.link && (
                <Image
                  src={contentImage.link}
                  alt="content image"
                  imgWidth={contentImage.width}
                  imgHeight={contentImage.height}
                />
              )}
              <WelcomeMessage
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(t('overlapMenu.welcomeMessage'))
                }}
              />
              <Menu>
                <MenuSubtitleWrapper textSize={size.menuSubtitleFontSize}>
                  {menuSubtitle.map(item => {
                    return (
                      <MenuSubtitle key={item} textSize={size.menuSubtitleFontSize}>
                        {t(item)}
                      </MenuSubtitle>
                    );
                  })}
                </MenuSubtitleWrapper>
                <OverlapMenuInnerWrapper
                  data={mainLayerItesm}
                  cardWidth={ui.isMobile ? mobileView.mainLayer.width : size.mainLayer.width}
                  cardHeight={ui.isMobile ? mobileView.mainLayer.height : size.mainLayer.height}
                  cardSidePadding={ui.isMobile ? mobileView.itemSidePadding : size.itemSidePadding}
                  cardImageSize={size.itemImage}
                  colors={itemColors}
                  onItemClick={handleClickMenuItem}
                  cardBoxShadow={colors.itemBoxShadow}
                />
              </Menu>
              {contentAdditionalText && (
                <AdditionalInformation
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(t(contentAdditionalText))
                  }}
                />
              )}
            </Wrapper>
          </StyledScrollbar>

          <ModalWrapper
            isOpen={ui.isOpenOverlapMenuModal}
            onBackdropClick={handleBackdropClick}
            BackdropProps={{ style: { backgroundColor: backdrop.backdropBackground } }}
            contentPadding={ui.isMobile ? mobileView.modalPadding : size.modalPadding}
          >
            <>
              {options.breadCrumbsEnabled && (
                <BreadCrumbsWrapper>
                  <BreadCrumbs>
                    {breadCrumbItems.map((crumb, indx) => {
                      return (
                        <BreadCrumb key={crumb} textColor={backdrop.color}>
                          {t(crumb)}
                          {indx < breadCrumbItems.length - 1 && (
                            <ChevronRightIcon fill={backdrop.color} />
                          )}
                        </BreadCrumb>
                      );
                    })}
                  </BreadCrumbs>
                </BreadCrumbsWrapper>
              )}
              <ModalContent onClick={e => e.currentTarget === e.target && handleBackdropClick()}>
                <OverlapMenuInnerWrapper
                  data={itemChildren}
                  cardWidth={ui.isMobile ? mobileView.innerLayers.width : size.innerLayers.width}
                  cardHeight={ui.isMobile ? mobileView.innerLayers.height : size.innerLayers.height}
                  cardImageSize={size.itemImage}
                  cardSidePadding={ui.isMobile ? mobileView.itemSidePadding : size.itemSidePadding}
                  colors={itemColors}
                  onItemClick={handleClickMenuItem}
                  onBackdropClick={handleBackdropClick}
                />
              </ModalContent>
              <BackButton textColor={backdrop.color} onClick={handleBackClick}>
                <BackIcon fill={backdrop.color} /> {t('back')}
              </BackButton>
            </>
          </ModalWrapper>
        </>
      )}
    </Root>
  );
};

OverlapMenuContent.propTypes = {
  openChat: PropTypes.func
};

OverlapMenuContent.defaultProps = {
  openChat: () => {}
};

const Root = styled.div`
  position: relative;
  flex: 1 0 auto;
  display: flex;
  height: 100%;
  padding: 10px;
  font: inherit;
  line-height: normal;

  .overlapMenuHome {
    padding: 0;
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: #2873c0;
    text-decoration: underline;
    background-color: transparent;
    border: none;
    outline: none;
    cursor: pointer;
  }
`;

const StyledScrollbar = styled(Scrollbar)`
  height: 100%;
`;

const ScrollbarView = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 0;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding: ${props => props.contentPadding};
  color: ${props => props.mainColor};
`;

const Image = styled.img`
  flex: 0 0 auto;
  display: block;
  margin-bottom: 15px;
  width: ${({ imgWidth }) => imgWidth || 70}px;
  height: ${({ imgHeight }) => (imgHeight ? `${imgHeight}px` : 'auto')};
`;

const WelcomeMessage = styled.div`
  margin: 0 0 20px;
  font-size: 14px;
  text-align: center;
`;

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  & > p {
    margin: 0 0 10px;
  }
`;

const MenuSubtitleWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 10px 10px;
  font-size: ${props => props.textSize || 12}px;
  color: #999;
  p {
    font: inherit;
  }
`;

const MenuSubtitle = styled.p`
  flex: 1 0 auto;
  font-size: ${props => props.textSize || 12}px;
  text-align: center;
  color: #999;
`;

const AdditionalInformation = styled.div`
  width: 100%;
  padding: 10px 20px;
  margin-top: 20px;
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
  color: #999;

  a,
  a:hover,
  a:focus {
    font: inherit;
    color: inherit;
  }
  p,
  span {
    font: inherit;
  }
`;

const ModalWrapper = styled(Modal)`
  flex-direction: column;
  padding: ${props => props.contentPadding || '10px'};
`;

const ModalContent = styled.div`
  flex: 1 0;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
`;

const BreadCrumbsWrapper = styled.div`
  width: 100%;
  padding-bottom: 4px;
`;

const BreadCrumbs = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  padding: 0;
  margin: 0;
`;

const BreadCrumb = styled.li`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  padding: 0;
  margin: 0;
  list-style: none;
  font-size: 14px;
  line-height: 1.2;
  color: ${props => props.textColor};
  &:last-of-type {
    font-weight: 700;
  }
  &:not(:last-of-type) {
    margin-right: 2px;
  }
  & svg {
    width: 9px;
    margin-left: 2px;
  }
`;

const BackButton = styled(ButtonBase)`
  padding-top: 4px;
  color: ${props => props.textColor};
  & svg {
    height: 16px;
  }
`;

export default observer(OverlapMenuContent);
